import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export type SubscriptionStatus =
  | "none"
  | "trialing"
  | "active"
  | "past_due"
  | "cancelled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid"
  | "paused";

export interface SubscriptionState {
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export function useSubscription() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["subscription", user?.id],
    queryFn: async (): Promise<SubscriptionState> => {
      if (!user) {
        return {
          status: "none",
          stripe_customer_id: null,
          stripe_subscription_id: null,
          stripe_price_id: null,
          current_period_end: null,
          cancel_at_period_end: false,
        };
      }
      const { data, error } = await supabase
        .from("user_settings")
        .select(
          "subscription_status, stripe_customer_id, stripe_subscription_id, stripe_price_id, stripe_current_period_end, stripe_cancel_at_period_end",
        )
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return {
        status: (data?.subscription_status as SubscriptionStatus) || "none",
        stripe_customer_id: data?.stripe_customer_id ?? null,
        stripe_subscription_id: data?.stripe_subscription_id ?? null,
        stripe_price_id: data?.stripe_price_id ?? null,
        current_period_end: data?.stripe_current_period_end ?? null,
        cancel_at_period_end: !!data?.stripe_cancel_at_period_end,
      };
    },
    enabled: !!user,
    refetchOnWindowFocus: true,
  });
}

export function useStartCheckout() {
  return useMutation({
    mutationFn: async (input?: { discount_code?: string }) => {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          discount_code: input?.discount_code,
          success_url: `${window.location.origin}/settings?checkout=success`,
          cancel_url: `${window.location.origin}/settings?checkout=cancelled`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error("Stripe did not return a checkout URL");
      window.location.href = data.url;
      return data.url as string;
    },
  });
}

export function useOpenPortal() {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: { return_url: `${window.location.origin}/settings` },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error("Stripe did not return a portal URL");
      window.location.href = data.url;
      return data.url as string;
    },
  });
}
